import React, { useMemo } from 'react';
import { useTheme } from '@/theme/hooks';
import { PhotoReferenceThumbnail } from './atomic/PhotoReferenceThumbnail';
import { ShapeReference } from './atomic/ShapeReference';
import { renderMarkdown } from '@/utils/markdownRenderer';
import type { Photo } from '../types/photo';
import type { HeroPhoto } from '../types/critique-session.types';
import type { MarkupShape } from '../types/markup.types';
import type { CritiqueEdit } from '../types/edit.types';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  // Feature 050: Shape reference interaction
  photo?: Photo | HeroPhoto;
  markup?: CritiqueEdit[];
  onShapeReferenceClick?: (shapeNumber: number) => void;
}

/**
 * MarkdownRenderer component
 * Feature: 036-markdown-critique-comments
 * Task: T027
 * Updated: Feature 069 - Client-side markdown rendering
 *
 * Renders raw markdown to sanitized HTML client-side.
 * Then parses the HTML to replace <span class="mention"> elements
 * with PhotoReferenceThumbnail React components for @-mention photo references.
 *
 * Uses Tailwind's `prose` classes for beautiful markdown styling.
 * Applies prose-invert in dark mode for proper text colors.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
  photo,
  markup,
  onShapeReferenceClick,
}) => {
  // Use theme if available, default to light if not (e.g., in tests)
  let isDark = false;
  try {
    const { effectiveTheme } = useTheme();
    isDark = effectiveTheme === 'dark';
  } catch {
    // Not in ThemeProvider context, use default light theme
    isDark = false;
  }

  // Feature 069: Render markdown to HTML client-side, then convert mentions to React components
  const renderedContent = useMemo(() => {
    if (!content) return null;

    // Feature 069: Render markdown to HTML client-side
    const htmlContent = renderMarkdown(content);

    // Parse the HTML string
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Convert HTML to React elements
    const convertNode = (node: Node): React.ReactNode => {
      // Text node
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      // Element node
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;

        // Special handling for mention spans (photo/user references)
        if (element.tagName === 'SPAN' && element.classList.contains('mention')) {
          const objectId = element.getAttribute('data-object-id');
          if (objectId) {
            return <PhotoReferenceThumbnail key={objectId} objectId={objectId} />;
          }
        }

        // Special handling for shape reference spans (@1-@9)
        // Feature 050: Interactive shape references
        if (element.tagName === 'SPAN' && element.classList.contains('shape-reference')) {
          const shapeNumber = parseInt(element.getAttribute('data-shape') || '0', 10);

          // If we have photo and markup data, render interactive component
          if (photo && markup && markup.length > 0 && shapeNumber >= 1 && shapeNumber <= 9) {
            // Collect all shapes from all markup (editData.shapes exists when editType is 'markup')
            const allShapes: MarkupShape[] = markup.flatMap(m => {
              const editData = m.editData as any;
              return editData?.shapes || [];
            });

            return (
              <ShapeReference
                key={`shape-${shapeNumber}`}
                shapeNumber={shapeNumber}
                photo={photo}
                shapes={allShapes}
                onClick={() => onShapeReferenceClick?.(shapeNumber)}
              />
            );
          }

          // Otherwise, render as plain styled span (fallback)
          // This preserves the blue styling even without interactivity
        }

        // Convert child nodes
        const children = Array.from(element.childNodes).map((child, index) =>
          <React.Fragment key={index}>{convertNode(child)}</React.Fragment>
        );

        // Create React element for this tag
        const tagName = element.tagName.toLowerCase();
        const props: Record<string, any> = {};

        // Copy attributes
        Array.from(element.attributes).forEach(attr => {
          // Convert class to className
          const propName = attr.name === 'class' ? 'className' : attr.name;
          props[propName] = attr.value;
        });

        return React.createElement(tagName, props, ...children);
      }

      return null;
    };

    // Convert body children
    return Array.from(doc.body.childNodes).map((node, index) => (
      <React.Fragment key={index}>{convertNode(node)}</React.Fragment>
    ));
  }, [content, photo, markup, onShapeReferenceClick]);

  return (
    <div className={`prose max-w-none ${isDark ? 'prose-invert' : ''} ${className}`}>
      {renderedContent}
    </div>
  );
};
